/** Labels, icons and colour tokens for database enums (one switch per enum, like the native ports). */
import type { FilterOperator, OptionColor, PropertyType, StatusGroup, ViewLayout } from "@bundle/contracts";
import type { MessageKey, PersonOption, RelationOption } from "@bundle/core";
import type { ColorToken, IconName } from "@bundle/tokens";

/** What cells need to show and edit values besides the row. */
export type CellContext = {
  people: PersonOption[];
  /** Page id → title (relation cells). */
  titles: Record<string, string>;
  /** Relation property id → pages to pick from. */
  relationOptions: Record<string, RelationOption[]>;
  /** First weekday of date pickers. */
  weekStartsOn: number;
  /** Local time offset for date-time cells (minutes to add to UTC). */
  tzOffsetMinutes: number;
  readOnly: boolean;
};

export const PROPERTY_TYPES: PropertyType[] = [
  "text", "number", "select", "multi_select", "status", "date", "person", "checkbox", "url", "email", "files", "relation", "contact",
  "formula", "rollup", "created_time", "created_by", "edited_time", "edited_by",
];

export const LAYOUTS: ViewLayout[] = ["board", "table", "list", "calendar", "sheet"];

export const OPTION_COLORS: OptionColor[] = ["default", "gray", "brown", "orange", "yellow", "green", "blue", "purple", "pink", "red"];

export function typeLabel(type: PropertyType): MessageKey {
  switch (type) {
    case "title":
      return "db.type.title";
    case "text":
      return "db.type.text";
    case "number":
      return "db.type.number";
    case "select":
      return "db.type.select";
    case "multi_select":
      return "db.type.multiSelect";
    case "status":
      return "db.type.status";
    case "date":
      return "db.type.date";
    case "person":
      return "db.type.person";
    case "checkbox":
      return "db.type.checkbox";
    case "url":
      return "db.type.url";
    case "email":
      return "db.type.email";
    case "files":
      return "db.type.files";
    case "relation":
      return "db.type.relation";
    case "contact":
      return "db.type.contact";
    case "created_time":
      return "db.type.createdTime";
    case "created_by":
      return "db.type.createdBy";
    case "edited_time":
      return "db.type.editedTime";
    case "edited_by":
      return "db.type.editedBy";
    case "formula":
      return "db.type.formula";
    case "rollup":
      return "db.type.rollup";
  }
}

export function typeIcon(type: PropertyType): IconName {
  switch (type) {
    case "title":
      return "format";
    case "text":
      return "text";
    case "number":
      return "hash";
    case "select":
      return "select";
    case "multi_select":
      return "multiSelect";
    case "status":
      return "status";
    case "date":
      return "calendar";
    case "person":
      return "user";
    case "checkbox":
      return "checkbox";
    case "url":
      return "link";
    case "email":
      return "at";
    case "files":
      return "attach";
    case "relation":
      return "relation";
    case "contact":
      return "users";
    case "created_time":
    case "edited_time":
      return "clock";
    case "created_by":
    case "edited_by":
      return "createdBy";
    case "formula":
      return "formula";
    case "rollup":
      return "rollup";
  }
}

export function layoutLabel(layout: ViewLayout): MessageKey {
  switch (layout) {
    case "board":
      return "db.layout.board";
    case "table":
      return "db.layout.table";
    case "list":
      return "db.layout.list";
    case "calendar":
      return "db.layout.calendar";
    case "sheet":
      return "db.layout.sheet";
  }
}

export function layoutIcon(layout: ViewLayout): IconName {
  switch (layout) {
    case "board":
      return "board";
    case "table":
      return "table";
    case "list":
      return "list";
    case "calendar":
      return "calendar";
    case "sheet":
      return "sheet";
  }
}

export function operatorLabel(op: FilterOperator): MessageKey {
  switch (op) {
    case "eq":
      return "db.op.eq";
    case "neq":
      return "db.op.neq";
    case "contains":
      return "db.op.contains";
    case "not_contains":
      return "db.op.notContains";
    case "starts_with":
      return "db.op.startsWith";
    case "ends_with":
      return "db.op.endsWith";
    case "is_empty":
      return "db.op.isEmpty";
    case "is_not_empty":
      return "db.op.isNotEmpty";
    case "gt":
      return "db.op.gt";
    case "gte":
      return "db.op.gte";
    case "lt":
      return "db.op.lt";
    case "lte":
      return "db.op.lte";
    case "before":
      return "db.op.before";
    case "after":
      return "db.op.after";
    case "on_or_before":
      return "db.op.onOrBefore";
    case "on_or_after":
      return "db.op.onOrAfter";
    case "past_week":
      return "db.op.pastWeek";
    case "next_week":
      return "db.op.nextWeek";
    case "has_any":
      return "db.op.hasAny";
    case "has_all":
      return "db.op.hasAll";
    case "has_none":
      return "db.op.hasNone";
    case "is_me":
      return "db.op.isMe";
  }
}

export function statusGroupLabel(group: StatusGroup): MessageKey {
  switch (group) {
    case "todo":
      return "db.statusGroup.todo";
    case "in_progress":
      return "db.statusGroup.inProgress";
    case "complete":
      return "db.statusGroup.complete";
  }
}

export function colorLabel(color: OptionColor): MessageKey {
  switch (color) {
    case "default":
      return "db.color.default";
    case "gray":
      return "db.color.gray";
    case "brown":
      return "db.color.brown";
    case "orange":
      return "db.color.orange";
    case "yellow":
      return "db.color.yellow";
    case "green":
      return "db.color.green";
    case "blue":
      return "db.color.blue";
    case "purple":
      return "db.color.purple";
    case "pink":
      return "db.color.pink";
    case "red":
      return "db.color.red";
  }
}

/** `tag.<color>.bg`. */
export function tagBackground(color: OptionColor): ColorToken {
  switch (color) {
    case "default":
      return "tag.default.bg";
    case "gray":
      return "tag.gray.bg";
    case "brown":
      return "tag.brown.bg";
    case "orange":
      return "tag.orange.bg";
    case "yellow":
      return "tag.yellow.bg";
    case "green":
      return "tag.green.bg";
    case "blue":
      return "tag.blue.bg";
    case "purple":
      return "tag.purple.bg";
    case "pink":
      return "tag.pink.bg";
    case "red":
      return "tag.red.bg";
  }
}

/** `tag.<color>.fg`. */
export function tagForeground(color: OptionColor): ColorToken {
  switch (color) {
    case "default":
      return "tag.default.fg";
    case "gray":
      return "tag.gray.fg";
    case "brown":
      return "tag.brown.fg";
    case "orange":
      return "tag.orange.fg";
    case "yellow":
      return "tag.yellow.fg";
    case "green":
      return "tag.green.fg";
    case "blue":
      return "tag.blue.fg";
    case "purple":
      return "tag.purple.fg";
    case "pink":
      return "tag.pink.fg";
    case "red":
      return "tag.red.fg";
  }
}

/** Names for a new status property's three options (translated by the caller). */
export function statusDefaultsKeys(): { notStarted: MessageKey; inProgress: MessageKey; done: MessageKey } {
  return { notStarted: "db.status.notStarted", inProgress: "db.status.inProgress", done: "db.status.done" };
}
